/**
 * 이상한 방 - 매치 세션 (전송 ↔ 씬·입력·카메라 접착층, 여럿이)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조) · 4.3절(탄환 경제) · 4.4절(원래 방)
 *            `이상한방-상태모델-v0.1.md` 9절(CPU 상대) · 11.2절(클라이언트가 가진 것) · 11.3절(서버가 쥐는 것)
 *            로드맵 M2(매치 세션 - `sessionWarp`·`sessionAudio`·`sessionReveal`·`ui/juice`·`ui/gameHud`를 그대로 재사용)
 *
 * ## 이 파일이 존재하는 이유
 *
 * `session.ts`(싱글)가 리듀서의 `Effect[]`를 화면으로 바꾸듯, 이 파일은 **전송(`Transport`)으로 받은 것**을 화면으로 바꾼다.
 * 규칙의 원본은 호스트(`net/matchHost.ts` = 리듀서 + 큐)에 있고, 그 호스트가 같은 프로세스(루프백)인지 소켓 너머(M6)인지
 * 이 파일은 모른다 - 그래서 봇 판에서 검증한 세션 코드가 서버 판에서 한 줄도 다르지 않다(브리프).
 *
 * 따라서 이 파일에도 **규칙이 하나도 없다.** "지금 쏠 수 있는가"조차 뷰의 불리언(`self.canShoot`)을 읽는다(로드맵 2.5절).
 *
 * ## 루프백에서도 클라이언트는 판정하지 않는다
 *
 * 탭은 기하까지만 하고 `shoot`을 보내며, 색·글자·자막탑은 `shotResolved`가 돌아온 뒤에 찍힌다. 그 전문과 코드는
 * `matchSessionInput.ts`(보내는 쪽)와 `matchSessionEffects.ts`(찍는 쪽) 머리말에 있다.
 *
 * ## 세 메시지, 세 자리 · 로비는 세션 밖이다
 *
 * `rooms`·`step`·`clock`이 어디에 꽂히는지는 `matchSessionInbox.ts` 머리말에 있다. M2~M5의 "welcome에 ready, 전원 준비면 start"
 * 두 줄은 M6에서 로비(`app/lobbyFlow.ts`)로 옮겨 갔다 - 이 세션은 로비 페이즈에 아무것도 보내지 않는다. 여기 남은 것은 보내는 문
 * (`send` - `cseq`를 매기는 유일한 자리)이고, 로비도 그 문(`act`)을 빌려 쓴다.
 *
 * ## 이 파일에서 갈라져 나간 것
 *
 * 전부 **세션 클로저가 아니라 인자만 보도록** 잘랐다(`session.ts`와 같은 결정) - 그래야 옮긴 것이 배선인지 규칙인지가 드러난다.
 *
 *  - `matchSessionView.ts`     `ClientView` → HUD·결과·개발 훅이 읽을 모양(산술 0)
 *  - `matchSessionEffects.ts`  효과 하나 → 씬·HUD·소리·연출(싱글의 `applyEffect` 자리)
 *  - `matchSessionPhases.ts`   페이즈 진입(싱글의 `onPhaseEnter` 자리 - 자막·시계 잠금·전환 소유권·프록시·리빌·결과). 효과 번역기가 품는다
 *  - `matchSessionInbox.ts`    받는 문 - `ServerMessage` 하나 → 뷰 교체 · `seq` 문 · 시계 앵커 · 거절 짝짓기
 *  - `matchSessionInput.ts`    입력 번역 - 탭 한 발 → 기하 → `shoot` · 버튼 → 이벤트
 *  - `matchSessionScene.ts`    두 방의 복원과 "지금 씬에 어느 방이 올라가 있는가"
 *  - `matchSessionChanger.ts`  사람 체인저의 배선(M4) - 도구가 쓰는 문 넷과 체인저 효과의 번역. 도구는 무대가 세운다
 *  - `matchSessionDecorate.ts` 꾸미기의 배선(M10) - 같은 모양의 문과 조각 단위 방 갱신(`placed`·`unplaced`)
 *  - `matchSessionParty.ts`    여럿이에만 있는 화면 여섯의 조립과 프레임 갱신(M5) - 관전 카메라 · 문 소리 · 리빌 이름표도 여기
 *  - `matchClock.ts`           `step.atMs` + `clock`으로 시계 보간(순수)
 *  여기 남은 것은 부품의 **조립**과 라운드 수명(`update` · `setActive` · `dispose`)이다 - 부품들이 서로를 순서로 붙잡고 있어서
 *  그 순서는 한 파일에 있어야 한다.
 *  싱글과 **공유**하는 것: `sessionWarp` · `sessionAudio` · `sessionReveal` · `sessionResult` · `sessionEffects` · `ui/juice` · `ui/gameHud`.
 *  여럿이에만 있는 화면 여섯(`ui/playerStrip` · `zoneProgress` · `wagerBanner` · `revealParty` · `spectateBar` · `doorQueue`)은 `matchSessionParty.ts`가
 *  전부 세운다 - 무엇이 뷰로 그려지고 무엇이 효과로 뜨는지는 그 머리말에. `ui/rankingList`는 결과 화면이 끼운다.
 */

import type { FloorPlan } from '../core/floorPlan'
import type { Catalog } from '../core/room'
import { MS_PER_SECOND } from '../core/time'
import type { PlayerId, RoomBounds, RoomState } from '../core/types'
import type { IsoCamera } from '../input/isoCamera'
import type { Transport } from '../net/transport'
import type { SceneCtx } from '../render/scene'
import { createRoomTransition } from '../render/transition'
import { HUNT_CURSOR, createGameHud, type NoticeTone } from '../ui/gameHud'
import { createJuice } from '../ui/juice'
import type { ClientView } from './match/matchView'
import type { MatchPhase, PlayerEvent } from './match/matchTypes'
import { createMatchClock, originStatusAfter, remainingAfter } from './matchClock'
import { createMatchChangerPort, type ChangerLink, type MatchChangerPort } from './matchSessionChanger'
import { createMatchDecoratePort, type DecorateLink, type MatchDecoratePort } from './matchSessionDecorate'
import { createMatchEffectHandler, type PendingShot } from './matchSessionEffects'
import { createMatchInbox } from './matchSessionInbox'
import { createMatchInput } from './matchSessionInput'
import { createMatchParty } from './matchSessionParty'
import { createMatchScene } from './matchSessionScene'
import { buildMatchDebugInfo, buildMatchHudView, isMeInOrigin } from './matchSessionView'
import { createSessionAudio, type SoundSink } from './sessionAudio'
import { createSessionResult } from './sessionResult'
import { createSessionReveal } from './sessionReveal'
import type { SessionDebugInfo } from './sessionView'
import { createWarpVeil, startWarpOver } from './sessionWarp'
import type { Tuning } from './tuning'

/* --- 상수 ------------------------------------------------------------------- */

/** 첫 `act`의 `cseq`. 거절(`rejected`)이 어느 요청의 것인지 짝지어 주는 번호라 0이 아니어도 된다. */
const FIRST_CSEQ = 1

/* --- 타입 ------------------------------------------------------------------- */

export type MatchSessionDeps = Readonly<{
  ctx: SceneCtx
  iso: IsoCamera
  bounds: RoomBounds
  catalog: Catalog
  plan: FloorPlan
  tuning: Tuning
  /** 호스트와 말하는 문. 루프백이든 소켓이든 이 세션은 모른다. **닫는 것은 이 세션이다**(`dispose`). */
  transport: Transport
  /**
   * 예외: `dispose`에서 전송을 닫지 않는다(로드맵 M11). 기본은 거짓 - 전송은 세션의 것이다.
   *
   * 참으로 오는 자리는 소켓 방의 로비 뒤 임시 무대 하나뿐이다(`app/stage.MatchStageRequest.retainTransport`).
   * 여럿이 평면도가 좌석 수의 함수라 판이 시작될 때 무대를 다시 세우는데, 그때 옛 무대가 소켓을 닫으면 방이 끊긴다.
   */
  retainTransport?: boolean | undefined
  /** 내 좌석. 호스트가 `by`로 찍는 신원이고, `foundChange.by`가 이것이면 내 것이다. */
  me: PlayerId
  /** 방 시드. 표시·개발 훅용이다 - 방은 호스트가 이 시드로 이미 만들었다. */
  seed: string
  mount: HTMLElement
  startActive?: boolean | undefined
  onMagazineUsed?: ((remaining: number) => void) | undefined
  onSound?: SoundSink | undefined
  /** 라운드가 준 핀(`roundRewards`). 계정에 넣는 것은 앱의 일이다 - 세션은 저장을 모른다. */
  onRoundRewards?: ((pins: number) => void) | undefined
  /** 결과 화면의 [나가기]. 없으면 버튼도 없다(`resultScreen.exitAction`). 방을 떠나는 것은 앱의 화면 전환이라 세션이 모른다. */
  onExitToSelect?: (() => void) | undefined
  /** 페이즈 진입 알림(M4). 무대가 바꾸기 진입에 체인저 도구를 세우고 수색 진입에 치운다(`matchSessionEffects.MatchEffectParts`). */
  onPhaseEnter?: ((phase: MatchPhase, previous: MatchPhase) => void) | undefined
  /** 지금 서 있는 체인저 도구의 조회(M4). 무대가 세우고 치우므로 세션은 값이 아니라 조회 함수를 받는다. 없으면 도구도 없다. */
  changer?: (() => ChangerLink | null) | undefined
  /** 지금 서 있는 꾸미기 도구의 조회(M10). 체인저와 같은 규약이고 두 도구는 배타적인 페이즈에 선다. */
  decorator?: (() => DecorateLink | null) | undefined
}>

export type MatchSession = Readonly<{
  /** 마지막으로 받은 뷰. `welcome` 전에는 `null`. 개발 훅(`__weirdRoom.match.view()`)의 원본이다. */
  getView: () => ClientView | null
  getDebugInfo: () => SessionDebugInfo
  getVisibleRoom: () => RoomState
  update: (dtSeconds: number) => void
  shootAt: (clientX: number, clientY: number) => void
  toggleOrigin: () => void
  useMagazine: () => void
  skipPhase: () => void
  showNotice: (text: string, tone?: NoticeTone) => void
  setActive: (active: boolean) => void
  isActive: () => boolean
  /** 무대가 체인저 도구를 세울 때 넘겨 주는 문 넷(`matchSessionChanger.ts`). 도구 자체는 세션이 만들지 않는다. */
  changer: MatchChangerPort
  /** 무대가 꾸미기 도구를 세울 때 넘겨 주는 문(`matchSessionDecorate.ts`). M10. */
  decorator: MatchDecoratePort
  /**
   * 이벤트 하나를 호스트로(M6). 로비(`app/lobbyFlow.ts`)의 [준비]·[시작]·설정이 이 문을 지난다 - `cseq`를 매기는 자리가 세션의
   * `send` 하나여야 거절(`rejected{cseq}`)이 어느 요청의 것인지 갈리지 않는다. 로비가 자기 번호로 전송에 직접 보내면 같은 번호의
   * 사격이 거절된 것으로 읽혀 기다리는 탭이 지워진다.
   */
  act: (event: PlayerEvent) => number
  dispose: () => void
}>

/* --- 본체 ------------------------------------------------------------------- */

export function createMatchSession(deps: MatchSessionDeps): MatchSession {
  const { ctx, iso, bounds, catalog, plan, tuning, transport, me } = deps
  const canvas = ctx.renderer.domElement

  let view: ClientView | null = null
  let active = deps.startActive ?? true
  let clockHeld = false
  let cursor = ''
  let cseq = FIRST_CSEQ
  const pendingShots: PendingShot[] = []
  const clock = createMatchClock()
  const sessionAudio = createSessionAudio(deps.onSound)
  const changerLink = (): ChangerLink | null => deps.changer?.() ?? null
  const decorateLink = (): DecorateLink | null => deps.decorator?.() ?? null

  /* --- 씬 반영 --- */

  /** 두 방과 "지금 씬에 무엇이 올라가 있는가"(`matchSessionScene.ts`). 방을 갈아 끼우는 권한은 여기서 넘긴 콜백들이다. */
  const scene = createMatchScene(ctx, catalog, plan.id)
  const showRoom = (toOrigin: boolean): void => scene.showRoom(toOrigin)

  /* --- 연출 부품(싱글과 같은 것들) --- */

  const warpVeil = createWarpVeil({
    mount: deps.mount,
    dissolveSeconds: tuning.warpDissolveSeconds,
    showRoom,
  })
  const transition = createRoomTransition(ctx)

  function startWarp(toOrigin: boolean, instant: boolean): void {
    // 전환이 아직 돌고 있으면 접고 디졸브를 건다 - 싱글과 같은 함수(`sessionWarp.startWarpOver`)에 순서의 이유가 있다.
    startWarpOver(transition, warpVeil, toOrigin, instant)
  }

  const hud = createGameHud({
    mount: deps.mount,
    handlers: {
      onToggleOrigin: () => input.toggleOrigin(),
      onSkipPhase: () => input.skipPhase(),
      onUseMagazine: () => input.useMagazine(),
      onRaise: () => input.raise(),
    },
    warpDissolveMs: tuning.warpDissolveSeconds * MS_PER_SECOND,
  })
  const juice = createJuice({ mount: deps.mount, punchTarget: hud.root })
  /**
   * 여럿이에만 있는 화면 여섯(`matchSessionParty.ts`). HUD와 같은 프레임에 같은 뷰로 그리고, 효과로 뜨는 둘(걸기 배너 · 관전 카메라)은
   * 효과 번역기가 부른다. 시점 버튼은 `send`로 `spectate`를 보낼 뿐이다 - 가부는 호스트다.
   */
  const party = createMatchParty({ mount: deps.mount, iso, catalog, juice, audio: sessionAudio, scene, send: (event) => send(event) })
  /** 켰다 끄는 화면 레이어 전부. 잠들 때 한 번에 접고 깨어날 때 한 번에 편다 - 하나가 빠지면 잠든 무대에 유령 자막이 남는다. */
  const layers = [hud, juice, ...party.layers] as const

  const result = createSessionResult({
    mount: deps.mount,
    tuning,
    level: null,
    // "한 판 더" = 다음 라운드. 방장만 통과한다(리듀서). 재도전은 없다(`SessionResultOptions.onRetrySameSeed` 주석).
    onPlayAgain: () => send({ type: 'nextRound' }),
    onExitToSelect: deps.onExitToSelect,
  })

  const reveal = createSessionReveal({
    ctx,
    iso,
    catalog,
    plan,
    secondsPerStep: tuning.revealSecondsPerChange,
    showLabel: (text, tone) => hud.showNotice(text, tone),
    playStepSound: () => sessionAudio.play('reveal'),
    // 스텝마다 누가 찾았는지(M5) - 이름표와 마지막 미발견의 소리(`matchSessionParty.onRevealStep`).
    onStepEnter: (step, index, steps) => party.onRevealStep(step, index, steps, requireView()),
    onFinished: () => {
      // 발견분까지 다 돌았으면 타이머를 기다리지 않는다. 방장이 아니면 거절될 뿐이다(남는 초는 호스트가 끝낸다).
      if (view?.phase === 'reveal') input.skipPhase()
    },
  })

  const effects = createMatchEffectHandler({
    me,
    tuning,
    catalog,
    ctx,
    hud,
    juice,
    audio: sessionAudio,
    reveal,
    revealParty: party.revealParty,
    result,
    transition,
    warpVeil,
    view: () => requireView(),
    rooms: scene.rooms,
    objectOf: scene.objectOf,
    decorate: scene.decorate,
    pendingShots,
    showRoom,
    startWarp,
    // 새 라운드의 첫 화면. 외우기 진입에만 불린다 - 이유는 `session.resetCamera`와 같다(수색을 다른 각도에서 시작하면 기준 기억이 값을 잃는다).
    resetCamera: () => iso.resetView(),
    holdClock: (held) => {
      clockHeld = held
    },
    hostNowMs: () => clock.hostNowMs(),
    isActive: () => active,
    onMagazineUsed: deps.onMagazineUsed,
    onRoundRewards: deps.onRoundRewards,
    onPhaseEnter: deps.onPhaseEnter,
    changer: changerLink,
    decorator: decorateLink,
    party,
  })

  function requireView(): ClientView {
    if (view === null) throw new Error('matchSession: 뷰가 오기 전에 효과가 왔다 - 호스트는 welcome을 먼저 보낸다')
    return view
  }

  /* --- HUD --- */

  function syncCursor(): void {
    const next =
      !active || view === null || view.phase !== 'hunt' ? '' : view.self.canShoot ? HUNT_CURSOR.aim : HUNT_CURSOR.blocked
    if (cursor === next) return
    cursor = next
    canvas.style.cursor = next
  }

  function render(): void {
    if (view !== null) {
      const since = clock.sinceStepMs()
      hud.render(
        buildMatchHudView(
          view,
          remainingAfter(view.phaseRemainingMs, since),
          originStatusAfter(view.self.origin, since),
          tuning.ammoPerMagazine,
        ),
      )
      party.render(view, since)
    }
    syncCursor()
  }

  /* --- 보내기 --- */

  /**
   * 이벤트 하나를 호스트로. 돌려주는 `cseq`로 거절을 짝짓는다. 닫힌 전송은 조용히 버린다(`Transport.send`).
   * `cseq`를 매기는 자리는 여기 하나다 - 입력(`matchSessionInput`)·받는 문(`matchSessionInbox`)·결과 화면 전부 이 문을 지난다.
   */
  function send(event: PlayerEvent): number {
    const id = cseq
    cseq += 1
    transport.send({ type: 'act', cseq: id, event })
    return id
  }

  /* --- 받기(`matchSessionInbox.ts`) --- */

  const inbox = createMatchInbox({
    transport,
    clock,
    scene,
    effects,
    hud,
    pendingShots,
    setView: (next) => {
      view = next
    },
    send,
    render,
    changer: changerLink,
    decorator: decorateLink,
  })

  /* --- 입력(`matchSessionInput.ts`) --- */

  const input = createMatchInput({
    ctx,
    iso,
    bounds,
    catalog,
    hud,
    warpVeil,
    scene,
    pendingShots,
    view: () => view,
    isActive: () => active,
    send,
  })

  /* --- 체인저의 문(`matchSessionChanger.ts`) --- */

  const changer = createMatchChangerPort({ view: () => view, scene, hud, send })

  /* --- 꾸미기의 문(`matchSessionDecorate.ts`) --- */

  const decorator = createMatchDecoratePort({ view: () => view, scene, hud, send })

  /* --- 프레임 --- */

  function update(dtSeconds: number): void {
    if (!active) return
    transition.update(dtSeconds)
    warpVeil.advance(dtSeconds)
    reveal.update(dtSeconds)

    const dtMs = dtSeconds * MS_PER_SECOND
    // 자막이 떠 있는 동안은 호스트 시계(루프백의 `transport.tick`)도 표시 시계도 밀지 않는다 - 둘이 갈리면 HUD 시계가 자막
    // 동안 앞서가다 다음 `step`에서 되튄다. 소켓에서는 서버가 계속 흐르지만 자막이 걷힌 뒤 첫 `clock`이 그 차이를 한 번에
    // 당긴다(`matchClock.ts` 머리말 "자막 잠금 동안의 드리프트").
    if (!clockHeld) {
      transport.tick(dtMs)
      clock.advance(dtMs)
    }
    render()
  }

  function setActive(next: boolean): void {
    if (active === next) return
    // 배치 모드로 나가기 전에 원래 방에서 나온다 - 잠든 판에서 원본을 무제한으로 보는 구멍(`session.setActive`).
    if (!next && view !== null && isMeInOrigin(view)) send({ type: 'leaveOrigin' })

    active = next
    for (const layer of layers) layer.setVisible(next)

    if (!next) {
      result.hide()
      effects.resetTransition()
      clockHeld = false
      // 배치 모드가 씬을 편집한다. 다시 들어올 때 무조건 다시 그려야 하므로 신뢰를 여기서 끊는다.
      scene.forget()
      warpVeil.cancel()
      syncCursor()
      return
    }

    if (view !== null) showRoom(isMeInOrigin(view))
    if (!sessionAudio.hasStartedBgm()) sessionAudio.startBgm()
    if (view?.phase === 'result') effects.showResult()
    render()
  }

  /* --- 초기 표시 --- */

  for (const layer of layers) layer.setVisible(active)
  if (active) sessionAudio.startBgm()
  render()

  return {
    getView: () => view,
    getDebugInfo: () => buildMatchDebugInfo(view, deps.seed),
    getVisibleRoom: () => scene.visibleRoom(view !== null && isMeInOrigin(view)),
    update,
    shootAt: input.shootAt,
    toggleOrigin: input.toggleOrigin,
    useMagazine: input.useMagazine,
    skipPhase: input.skipPhase,
    showNotice: (text, tone) => hud.showNotice(text, tone),
    setActive,
    isActive: () => active,
    changer,
    decorator,
    act: send,

    dispose(): void {
      inbox.dispose()
      // 전송은 이 세션의 것이다. 닫지 않으면 루프백 호스트가 다음 무대 뒤에서 계속 돈다.
      // 예외는 로비 뒤의 임시 무대 하나(`retainTransport`) - 그 소켓의 주인은 아직 로비다.
      if (deps.retainTransport !== true) transport.close()
      reveal.stop()
      transition.dispose()
      sessionAudio.stop()
      for (const layer of layers) layer.dispose()
      result.dispose()
      warpVeil.dispose()
      canvas.style.cursor = ''
    },
  }
}
